import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  addContactApi,
  createTicketApi,
  getTicketByIdApi,
  getTicketsByContactApi,
  getTicketConversationApi,
  postTicketReplyApi,
  postTicketAttachmentApi,
  getTicketAttachmentApi,
} from "./ticketApi";
import { getSecureItem, setSecureItem } from "../../utilsFunctions";
import { getCompanyProfile } from "../../api";
import { companylogo } from "../../assets/images";
import { Stack } from "@mui/material";
import Sidebar from "../sidebar/sidebar";

// ─── Design tokens (matches Tickets palette) ─────────────────────────────────
const T = {
  bg: "#F8FAFC",
  white: "#fff",
  border: "#E2E8F0",
  borderLight: "#F1F5F9",
  text: "#0F172A",
  textMid: "#1E293B",
  textMuted: "#64748B",
  textFaint: "#94A3B8",
  accent: "#00D95E",
  accentDark: "#01a046",
  accentBg: "#F0FDFA",
  accentText: "#01a046",
  danger: "#DC2626",
  dangerBg: "#FFF5F5",
  dangerBorder: "#FEE2E2",
  warning: "#D97706",
  warningBg: "#FFFBEB",
  warningBorder: "#FDE68A",
  warningText: "#92400E",
  successBg: "#F0FDF4",
  successBorder: "#BBF7D0",
  successText: "#166534",
  info: "#2563EB",
  infoBg: "#EFF6FF",
  infoBorder: "#BFDBFE",
  infoText: "#1D4ED8",
  purple: "#7C3AED",
  purpleBg: "#F5F3FF",
  shadow: "0 1px 4px rgba(15,23,42,0.06)",
  shadowMd: "0 4px 16px rgba(15,23,42,0.1)",
  shadowModal: "0 20px 60px rgba(15,23,42,0.18)",
  radius: "14px",
  radiusSm: "10px",
  radiusXs: "8px",
  font: "'Lato', 'Inter', 'Segoe UI', system-ui, sans-serif",
};

// Priority config — color codes the left border strip (the signature element)
const PRIORITY = {
  High: {
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
    text: "#B45309",
    strip: "#F59E0B",
  },
  Medium: {
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    text: "#1D4ED8",
    strip: "#3B82F6",
  },
  Low: {
    color: "#64748B",
    bg: "#F8FAFC",
    border: "#E2E8F0",
    text: "#475569",
    strip: "#94A3B8",
  },
};

const STATUS = {
  Open: { bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", dot: "#3B82F6" },
  Closed: { bg: "#F8FAFC", text: "#475569", border: "#E2E8F0", dot: "#94A3B8" },
};

const CATEGORIES = [
  { label: "How-to / Usage", departmentId: "1376762000000635037" },
  { label: "Bug / Not Working", departmentId: "1376762000000646619" },
  { label: "Feature Request", departmentId: "1376762000000651201" },
  { label: "Account / Access", departmentId: "1376762000000654783" },
  { label: "Performance", departmentId: "1376762000000658365" },
  { label: "Other", departmentId: "1376762000000661947" },
];
const PRIORITIES = ["Low", "Medium", "High"];
const STATUSES = ["Open", "Closed"];

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10MB — keep in sync with backend multer limit

// ─── Global CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; }

  .tkt-input:focus {
    border-color: ${T.accent} !important;
    background: ${T.white} !important;
    box-shadow: 0 0 0 3px rgba(13,148,136,0.12);
    outline: none;
  }
  .tkt-textarea:focus {
    border-color: ${T.accent} !important;
    background: ${T.white} !important;
    box-shadow: 0 0 0 3px rgba(13,148,136,0.12);
    outline: none;
  }
  .tkt-select:focus { outline: none; border-color: ${T.accent} !important; box-shadow: 0 0 0 3px rgba(13,148,136,0.12); }

  /* Table rows — left strip is the signature element */
  .tkt-row { transition: background 0.12s; cursor: pointer; }
  .tkt-row:hover td { background: #F8FAFC !important; }
  .tkt-row:hover .tkt-strip { width: 4px !important; }

  .tkt-tab { transition: all 0.15s; cursor: pointer; border: none; background: none; }
  .tkt-tab:hover { color: ${T.text} !important; }

  .tkt-btn-primary:hover { background: ${T.accentDark} !important; }
  .tkt-btn-primary:active { transform: scale(0.97); }
  .tkt-btn-outline:hover { border-color: #CBD5E1 !important; color: ${T.text} !important; background: ${T.bg} !important; }
  .tkt-btn-cancel:hover { background: ${T.bg} !important; }
  .tkt-icon-btn:hover { border-color: ${T.accent} !important; color: ${T.accent} !important; background: ${T.accentBg} !important; }
  .tkt-icon-danger:hover { background: ${T.dangerBg} !important; border-color: ${T.danger} !important; color: ${T.danger} !important; }

  .tkt-stat-card:hover { border-color: ${T.accent} !important; box-shadow: 0 4px 16px rgba(13,148,136,0.1) !important; transform: translateY(-1px); }
  .tkt-stat-card { transition: all 0.15s; }

  .tkt-modal-enter { animation: tktModalPop 0.22s ease; }
  @keyframes tktModalPop {
    from { opacity: 0; transform: scale(0.96) translateY(8px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
  .tkt-drawer-enter { animation: tktDrawerSlide 0.25s ease; }
  @keyframes tktDrawerSlide {
    from { opacity: 0; transform: translateX(32px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .tkt-alert-enter { animation: tktAlertIn 0.22s ease; }
  @keyframes tktAlertIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .tkt-spin { animation: tktSpin 0.8s linear infinite; }
  @keyframes tktSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  .tkt-table-wrap { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .tkt-table-wrap table { min-width: 700px; }

  .tkt-drawer-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.35); z-index: 1000; backdrop-filter: blur(2px); display: flex; justify-content: flex-end; }
  .tkt-drawer { height: 100vh; background: ${T.white}; width: min(520px, 100vw); overflow-y: auto; box-shadow: -8px 0 40px rgba(15,23,42,0.14); }

  .tkt-priority-select label:hover { border-color: currentColor !important; opacity: 0.85; }

  .tkt-comment { animation: tktAlertIn 0.2s ease; }

  .tkt-skeleton {
    background: linear-gradient(90deg, ${T.borderLight} 25%, ${T.border} 37%, ${T.borderLight} 63%);
    background-size: 400% 100%;
    animation: tktShimmer 1.4s ease infinite;
    border-radius: 4px;
  }
  @keyframes tktShimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }

  /* Scrollbar */
  .tkt-drawer::-webkit-scrollbar { width: 4px; }
  .tkt-drawer::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
  .tkt-comment-area::-webkit-scrollbar { width: 4px; }
`;

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const Ic = {
  Upload: () => (
    <svg
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M12 16V4" />
      <path d="M8 8l4-4 4 4" />
      <path d="M4 20h16" />
    </svg>
  ),
  Plus: () => (
    <svg
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Search: () => (
    <svg
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  Filter: () => (
    <svg
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  X: () => (
    <svg
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Eye: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Chat: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Tag: () => (
    <svg
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  ),
  User: () => (
    <svg
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Clock: () => (
    <svg
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Send: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  Ticket: () => (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z" />
    </svg>
  ),
  Alert: () => (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="#D97706"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  Check: () => (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="#16A34A"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  ArrowR: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Attach: () => (
    <svg
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  ),
  Spinner: () => (
    <svg
      className="tkt-spin"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      viewBox="0 0 24 24"
    >
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  ),
  File: () => (
    <svg
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const handleAttachmentClick = async (ticketId, attachment) => {
  try {
    const res = await getTicketAttachmentApi(ticketId, attachment.id);

    const blob = new Blob([res.data], {
      type: res.headers["content-type"],
    });

    const url = window.URL.createObjectURL(blob);

    window.open(url, "_blank");

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  } catch (err) {
    console.error(err);
  }
};
function htmlToText(input) {
  if (!input) return "";

  // If there are no HTML tags, return as is
  if (!/<\/?[a-z][\s\S]*>/i.test(input)) {
    return input.trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(input, "text/html");

  return (doc.body.textContent || "").trim();
}
function formatDate(date) {
  if (!date) return "—";

  const d = new Date(date);

  const pad = (n) => String(n).padStart(2, "0");

  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
function timeAgo(s) {
  if (!s) return "—";
  const diff = Math.floor((Date.now() - new Date(s)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${diff}d ago`;
}
function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Maps a raw Zoho ticket object to the shape the list/table expects
function mapZohoTicket(t) {
  const matchedCategory = CATEGORIES.find(
    (c) => c.departmentId === t.departmentId,
  );

  return {
    id: t.id, // Zoho's internal ticket ID — used for all API calls (conversation, reply)
    displayId: t.ticketNumber || t.id, // friendlier ID shown in the UI
    title: t.subject,
    description: t.description || "",
    category: matchedCategory?.label || "Other",
    priority: t.priority || "Medium",
    status: t.status || "Open",
    createdAt: t.createdTime,
    updatedAt: t.modifiedTime,
    departmentId: t.departmentId,
    comments: [], // loaded lazily per-ticket via getTicketConversationApi
  };
}

// Builds a "good enough" raw-shaped ticket object from the already-fetched
// list row, so the drawer can render instantly instead of waiting on a
// second network round trip just to open.
function buildPlaceholderTicket(mapped) {
  return {
    id: mapped.id,
    ticketNumber: mapped.displayId,
    subject: mapped.title,
    description: mapped.description,
    priority: mapped.priority,
    status: mapped.status,
    createdTime: mapped.createdAt,
    modifiedTime: mapped.updatedAt,
    departmentId: mapped.departmentId,
  };
}

let _alertId = 0;

// ─── Sub-components ───────────────────────────────────────────────────────────

function Toast({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginBottom: "16px",
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="tkt-alert-enter"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "11px 14px",
            background: t.type === "success" ? T.successBg : T.warningBg,
            border: `1.5px solid ${t.type === "success" ? T.successBorder : T.warningBorder}`,
            borderRadius: T.radiusSm,
            color: t.type === "success" ? T.successText : T.warningText,
          }}
        >
          {t.type === "success" ? <Ic.Check /> : <Ic.Alert />}
          <span style={{ flex: 1, fontSize: "13.5px", fontWeight: 500 }}>
            {t.msg}
          </span>
          <button
            onClick={() => onDismiss(t.id)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "inherit",
              display: "flex",
            }}
          >
            <Ic.X />
          </button>
        </div>
      ))}
    </div>
  );
}

function PriorityBadge({ p }) {
  const c = PRIORITY[p] || PRIORITY.Low;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "3px 9px",
        borderRadius: "20px",
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.text,
        fontSize: "12px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        fontFamily: T.font,
      }}
    >
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: c.strip,
          flexShrink: 0,
        }}
      />
      {p}
    </span>
  );
}

function StatusBadge({ s }) {
  const c = STATUS[s] || STATUS.Open;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "3px 9px",
        borderRadius: "20px",
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.text,
        fontSize: "12px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        fontFamily: T.font,
      }}
    >
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: c.dot,
          flexShrink: 0,
        }}
      />
      {s}
    </span>
  );
}

function CategoryBadge({ cat }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 8px",
        borderRadius: "6px",
        background: T.bg,
        border: `1px solid ${T.border}`,
        color: T.textMuted,
        fontSize: "11.5px",
        fontWeight: 500,
        whiteSpace: "nowrap",
        fontFamily: T.font,
      }}
    >
      <Ic.Tag /> {cat}
    </span>
  );
}

// ─── Shared style objects ─────────────────────────────────────────────────────
const labelStyle = {
  fontSize: "11.5px",
  fontWeight: 600,
  color: T.textMuted,
  display: "block",
  marginBottom: "6px",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  fontFamily: T.font,
};
const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  border: `1.5px solid ${T.border}`,
  borderRadius: T.radiusSm,
  fontSize: "14px",
  color: T.text,
  background: T.bg,
  fontFamily: T.font,
};
const selectStyle = {
  ...inputStyle,
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  paddingRight: "32px",
  cursor: "pointer",
};
const primaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "9px 18px",
  border: "none",
  borderRadius: T.radiusSm,
  background: T.accent,
  color: T.white,
  fontSize: "13.5px",
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 1px 3px rgba(13,148,136,0.25)",
  whiteSpace: "nowrap",
  fontFamily: T.font,
};
const cancelBtnStyle = {
  padding: "9px 18px",
  border: `1.5px solid ${T.border}`,
  borderRadius: T.radiusSm,
  background: T.white,
  color: T.textMuted,
  fontSize: "13.5px",
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: T.font,
};
const iconBtnStyle = {
  width: "30px",
  height: "30px",
  border: `1.5px solid ${T.border}`,
  borderRadius: T.radiusXs,
  background: T.white,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: T.textMuted,
};

// ─── Create Ticket Modal ──────────────────────────────────────────────────────
function CreateModal({ open, onClose, onCreate, contactId }) {
  const blank = {
    title: "",
    description: "",
    category: CATEGORIES[0].label,
    priority: "Medium",
  };
  const [form, setForm] = useState(blank);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setErr("Title is required.");
      return;
    }
    if (!form.description.trim()) {
      setErr("Description is required.");
      return;
    }
    if (!contactId) {
      setErr("No linked contact found yet. Please try again in a moment.");
      return;
    }

    const matchedCategory = CATEGORIES.find((c) => c.label === form.category);
    if (!matchedCategory) {
      setErr("Invalid category selected.");
      return;
    }

    setErr("");
    setSubmitting(true);

    const res = await createTicketApi({
      departmentId: matchedCategory.departmentId,
      contactId,
      subject: form.title,
      description: form.description,
      priority: form.priority,
    });

    setSubmitting(false);

    if (res?.data?.status) {
      onCreate(mapZohoTicket(res.data.result));
      setForm(blank);
      onClose();
    } else {
      setErr(res?.data?.msg || "Failed to create ticket. Please try again.");
    }
  };

  const handleClose = () => {
    setForm(blank);
    setErr("");
    onClose();
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.38)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "16px",
        backdropFilter: "blur(2px)",
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className="tkt-modal-enter"
        style={{
          background: T.white,
          borderRadius: T.radius,
          width: "100%",
          maxWidth: "560px",
          boxShadow: T.shadowModal,
          overflow: "hidden",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 22px 14px",
            borderBottom: `1px solid ${T.borderLight}`,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <span style={{ color: T.accent }}>
            <Ic.Ticket />
          </span>
          <h2
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: 700,
              color: T.text,
              flex: 1,
              fontFamily: T.font,
            }}
          >
            Raise a Ticket
          </h2>
          <button
            onClick={handleClose}
            style={{
              width: "28px",
              height: "28px",
              border: "none",
              background: T.borderLight,
              borderRadius: T.radiusXs,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: T.textMuted,
            }}
          >
            <Ic.X />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: "18px 22px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          {err && (
            <div
              style={{
                padding: "10px 14px",
                background: T.dangerBg,
                border: `1px solid ${T.dangerBorder}`,
                borderRadius: T.radiusXs,
                color: T.danger,
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {err}
            </div>
          )}

          {/* Title */}
          <div>
            <label style={labelStyle}>
              Title <span style={{ color: T.danger }}>*</span>
            </label>
            <input
              className="tkt-input"
              style={inputStyle}
              placeholder="Brief summary of the issue…"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
            />
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>
              Description <span style={{ color: T.danger }}>*</span>
            </label>
            <textarea
              className="tkt-textarea"
              style={{
                ...inputStyle,
                height: "100px",
                resize: "vertical",
                lineHeight: 1.6,
              }}
              placeholder="Describe the issue in detail — steps to reproduce, what you expected, what happened…"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>

          {/* Category + Priority row */}
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 180px" }}>
              <label style={labelStyle}>Category</label>
              <select
                className="tkt-select"
                style={selectStyle}
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.departmentId} value={c.label}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Priority selector */}
          <div>
            <label style={labelStyle}>Priority</label>
            <div
              className="tkt-priority-select"
              style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
            >
              {PRIORITIES.map((p) => {
                const c = PRIORITY[p];
                const sel = form.priority === p;
                return (
                  <label
                    key={p}
                    onClick={() => set("priority", p)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "8px",
                      border: `2px solid ${sel ? c.strip : T.border}`,
                      background: sel ? c.bg : T.white,
                      cursor: "pointer",
                      color: sel ? c.text : T.textMuted,
                      fontSize: "13px",
                      fontWeight: sel ? 600 : 400,
                      transition: "all 0.15s",
                      fontFamily: T.font,
                    }}
                  >
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: c.strip,
                      }}
                    />
                    {p}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Attachment note */}
          <div
            style={{
              padding: "10px 14px",
              background: T.accentBg,
              border: `1px solid #99F6E4`,
              borderRadius: T.radiusXs,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: T.accentText,
              fontSize: "12.5px",
              fontFamily: T.font,
            }}
          >
            <Ic.Attach />
            <span>
              File attachments can be added via comment after the ticket is
              created.
            </span>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 22px",
            borderTop: `1px solid ${T.borderLight}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            background: T.bg,
          }}
        >
          <button
            className="tkt-btn-cancel"
            onClick={handleClose}
            style={cancelBtnStyle}
          >
            Cancel
          </button>
          <button
            className="tkt-btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
            style={{ ...primaryBtnStyle, opacity: submitting ? 0.6 : 1 }}
          >
            <Ic.Plus /> {submitting ? "Submitting…" : "Submit Ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Ticket Detail Drawer ─────────────────────────────────────────────────────
function TicketDrawer({ ticket, onClose, refreshing }) {
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState(ticket?.status || "Open");
  const [conversation, setConversation] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const fileInputRef = useRef(null);

  const matchedCategory = CATEGORIES?.find(
    (c) => c?.departmentId == ticket?.departmentId,
  );

  useEffect(() => {
    if (!ticket) return;
    setStatus(ticket.status);

    loadConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]);

  const loadConversation = async () => {
    setConvLoading(true);
    const res = await getTicketConversationApi(ticket.id);
    if (res?.data?.status) {
      setConversation(res.data.result?.slice(1) || []);
    }
    setConvLoading(false);
  };

  if (!ticket) return null;

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;

    if (file.size > MAX_ATTACHMENT_SIZE) {
      setFileErr("File is too large (max 10MB).");
      return;
    }
    setFileErr("");
    setPendingFile(file);
  };

  const removePendingFile = () => {
    setPendingFile(null);
    setFileErr("");
  };

  const handleComment = async () => {
    if ((!comment.trim() && !pendingFile) || sending) return;
    setSending(true);

    // Comments require text in Zoho — if only a file was picked, send a
    // minimal placeholder message so the attachment has somewhere to attach to
    const messageToSend = comment.trim() || "Sent an attachment";
    const res = await postTicketReplyApi(ticket.id, messageToSend);

    if (res?.data?.status) {
      if (pendingFile) {
        const uploadRes = await postTicketAttachmentApi(ticket.id, pendingFile);
        if (!uploadRes?.data?.status) {
          setFileErr(
            uploadRes?.data?.msg ||
              "Message sent, but attachment upload failed.",
          );
        }
      }

      setComment("");
      setPendingFile(null);
      await loadConversation(); // re-fetch so the new reply + attachment show with Zoho's own data
    }

    setSending(false);
  };

  const strip = PRIORITY[ticket.priority]?.strip || T.accent;

  return (
    <div
      className="tkt-drawer-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="tkt-drawer tkt-drawer-enter">
        {/* Colored top strip — priority color */}
        <div style={{ height: "4px", background: strip }} />

        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${T.borderLight}`,
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: T.textFaint,
                  letterSpacing: "0.06em",
                  fontFamily: T.font,
                }}
              >
                {ticket.ticketNumber}
              </span>
              <StatusBadge s={status} />
              <PriorityBadge p={ticket.priority} />
              {refreshing && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "11px",
                    color: T.textFaint,
                    fontFamily: T.font,
                  }}
                >
                  <Ic.Spinner /> Syncing…
                </span>
              )}
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: "15px",
                fontWeight: 700,
                color: T.text,
                lineHeight: 1.4,
                fontFamily: T.font,
              }}
            >
              {ticket.subject}
            </h2>
          </div>
          <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
            <button
              onClick={() => {
                setConversation([]);
                onClose();
              }}
              style={{
                width: "30px",
                height: "30px",
                border: "none",
                background: T.borderLight,
                borderRadius: T.radiusXs,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: T.textMuted,
              }}
            >
              <Ic.X />
            </button>
          </div>
        </div>

        {/* Meta info */}
        <div
          style={{
            padding: "14px 20px",
            borderBottom: `1px solid ${T.borderLight}`,
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {[
            {
              icon: <Ic.Tag />,
              label: "Category",
              val: matchedCategory?.label || "N/A",
            },
            {
              icon: <Ic.Clock />,
              label: "Created",
              val: formatDate(ticket.createdTime),
            },
            {
              icon: <Ic.Clock />,
              label: "Updated",
              val: formatDate(ticket.modifiedTime),
            },
          ].map((m) => (
            <div
              key={m.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12.5px",
                color: T.textMuted,
                minWidth: "45%",
                fontFamily: T.font,
              }}
            >
              <span style={{ color: T.textFaint }}>{m.icon}</span>
              <span style={{ color: T.textFaint }}>{m.label}:</span>
              <span style={{ color: T.textMid, fontWeight: 500 }}>{m.val}</span>
            </div>
          ))}
        </div>

        {/* Description */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${T.borderLight}`,
          }}
        >
          <p
            style={{
              margin: "0 0 8px",
              fontSize: "11.5px",
              fontWeight: 600,
              color: T.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontFamily: T.font,
            }}
          >
            Description
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: T.textMid,
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              fontFamily: T.font,
            }}
          >
            {htmlToText(ticket.description)}
          </p>
        </div>

        {/* Conversation (real data via getTicketConversationApi) */}
        <div style={{ padding: "16px 20px", flex: 1 }}>
          <p
            style={{
              margin: "0 0 12px",
              fontSize: "11.5px",
              fontWeight: 600,
              color: T.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontFamily: T.font,
            }}
          >
            <Ic.Chat /> Conversation ({conversation.length})
          </p>

          {convLoading && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginBottom: "14px",
              }}
            >
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="tkt-skeleton"
                  style={{ height: "56px", borderRadius: T.radiusSm }}
                />
              ))}
            </div>
          )}

          {!convLoading && conversation.length === 0 && (
            <p
              style={{
                fontSize: "13px",
                color: T.textFaint,
                fontFamily: T.font,
              }}
            >
              No messages yet. Add one below.
            </p>
          )}

          {!convLoading && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginBottom: "14px",
              }}
            >
              {conversation.map((c) => (
                <div
                  key={c.id}
                  className="tkt-comment"
                  style={{
                    padding: "10px 14px",
                    background: T.bg,
                    border: `1px solid ${T.border}`,
                    borderRadius: T.radiusSm,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "5px",
                      flexWrap: "wrap",
                      gap: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: T.accent,
                        fontFamily: T.font,
                      }}
                    >
                      {c.senderName || c.sender}
                    </span>
                    <span
                      style={{
                        fontSize: "11.5px",
                        color: T.textFaint,
                        fontFamily: T.font,
                      }}
                    >
                      {formatDate(c.time)}
                    </span>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13.5px",
                      color: T.textMid,
                      lineHeight: 1.6,
                      fontFamily: T.font,
                    }}
                  >
                    {htmlToText(c.message)}
                  </p>

                  {c.type === "attachment" && c.attachment && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                        marginTop: "8px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          handleAttachmentClick(ticket?.id, c.attachment)
                        }
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 10px",
                          background: T.white,
                          border: `1px solid ${T.border}`,
                          borderRadius: T.radiusXs,
                          fontSize: "12px",
                          color: T.accentText,
                          fontFamily: T.font,
                          width: "fit-content",
                          cursor: "pointer",
                        }}
                      >
                        <Ic.File />
                        {c.attachment.name || "Attachment"}
                        {c.attachment.size ? (
                          <span style={{ color: T.textFaint }}>
                            ({formatFileSize(c.attachment.size)})
                          </span>
                        ) : null}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pending attachment preview */}
          {pendingFile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                background: T.accentBg,
                border: `1px solid #99F6E4`,
                borderRadius: T.radiusXs,
                marginBottom: "8px",
                fontSize: "12.5px",
                color: T.accentText,
                fontFamily: T.font,
              }}
            >
              <Ic.File />
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {pendingFile.name}
              </span>
              <span style={{ color: T.textFaint }}>
                {formatFileSize(pendingFile.size)}
              </span>
              <button
                onClick={removePendingFile}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: T.textMuted,
                  display: "flex",
                }}
                title="Remove attachment"
              >
                <Ic.X />
              </button>
            </div>
          )}

          {fileErr && (
            <p
              style={{
                margin: "0 0 8px",
                fontSize: "12px",
                color: T.danger,
                fontFamily: T.font,
              }}
            >
              {fileErr}
            </p>
          )}

          {/* Add reply */}
          <div style={{ display: "flex", gap: "8px" }}>
            <textarea
              className="tkt-textarea tkt-comment-area"
              style={{ ...inputStyle, flex: 1, height: "70px", resize: "none" }}
              placeholder="Write a reply…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey))
                  handleComment();
              }}
            />
            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={handleFilePick}
              />
              <button
                className="tkt-btn-primary"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  ...primaryBtnStyle,
                  alignSelf: "flex-start",
                  padding: "10px 14px",
                  background: pendingFile ? T.accentDark : T.accent,
                }}
                title="Attach a file"
              >
                <Ic.Upload />
              </button>
              <button
                className="tkt-btn-primary"
                onClick={handleComment}
                disabled={sending}
                style={{
                  ...primaryBtnStyle,
                  alignSelf: "flex-end",
                  padding: "10px 14px",
                  opacity: sending ? 0.6 : 1,
                }}
                title="Send"
              >
                {sending ? <Ic.Spinner /> : <Ic.Send />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Tickets() {
  const loggedinUser = useMemo(() => getSecureItem("loggedinUser"), []);

  const [contactId, setContactId] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tabStatus, setTabStatus] = useState("All");
  const [filterPriority, setPriority] = useState("All");
  const [filterCategory, setCategory] = useState("All");
  const [createOpen, setCreateOpen] = useState(false);
  const [activeTicket, setActive] = useState(null);
  const [ticketRefreshing, setTicketRefreshing] = useState(false);
  const [loadingRowId, setLoadingRowId] = useState(null);
  const [toasts, setToasts] = useState([]);

  const pushToast = useCallback((msg, type = "success") => {
    const id = ++_alertId;
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 4000);
  }, []);

  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );

  // ── Init: resolve contactId (cache-first), then fetch tickets ──
  useEffect(() => {
    initContactAndTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initContactAndTickets = async () => {
    setLoading(true);
    try {
      let cId = getSecureItem("contactId");

      if (!cId) {
        const compData = await getCompanyProfile();

        if (compData?.contactId) {
          cId = compData.contactId;
        } else {
          // No Zoho contact linked yet — create one from company profile data
          const contactRes = await addContactApi({
            companyId: compData?.companyId,
            firstName: loggedinUser?.firstName,
            lastName: loggedinUser?.lastName || "Company",
            email: loggedinUser?.userEmail || "",
            phone: loggedinUser?.userPhone || "",
          });

          if (contactRes?.data?.status) {
            cId = contactRes.data.result?.id;
          } else {
            pushToast(
              contactRes?.data?.msg || "Failed to link Zoho contact.",
              "warning",
            );
          }
        }

        if (cId) setSecureItem("contactId", cId);
      }

      setContactId(cId || null);

      if (cId) {
        await fetchTickets(cId);
      }
    } catch (error) {
      console.log({ error });
      pushToast("Failed to load tickets.", "warning");
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async (cId) => {
    const res = await getTicketsByContactApi(cId);
    if (res?.data?.status) {
      setTickets((res.data.result || []).map(mapZohoTicket));
    } else {
      pushToast(res?.data?.msg || "Failed to fetch tickets.", "warning");
    }
  };

  // ── View a ticket: open the drawer INSTANTLY with data already in
  // hand from the list, then fetch the authoritative record in the
  // background and patch it in once it resolves. Removes the perceived
  // lag from waiting on a network round trip just to open the drawer. ──
  const handleViewTicket = useCallback(
    async (t) => {
      setActive(buildPlaceholderTicket(t));
      setTicketRefreshing(true);
      setLoadingRowId(t.id);

      try {
        const res = await getTicketByIdApi(t.id);
        if (res?.data?.status) {
          setActive(res.data.result);
        } else {
          pushToast(
            res?.data?.msg || "Failed to refresh ticket details.",
            "warning",
          );
        }
      } catch (error) {
        console.log({ error });
      } finally {
        setTicketRefreshing(false);
        setLoadingRowId(null);
      }
    },
    [pushToast],
  );

  // Stats
  const stats = useMemo(
    () => ({
      All: tickets.length,
      Open: tickets.filter((t) => t.status === "Open").length,
      Closed: tickets.filter((t) => t.status === "Closed").length,
    }),
    [tickets],
  );

  // Filtered list
  const filtered = useMemo(
    () =>
      tickets.filter((t) => {
        const matchTab = tabStatus === "All" || t.status === tabStatus;
        const matchP =
          filterPriority === "All" || t.priority === filterPriority;
        const matchCat =
          filterCategory === "All" || t.category === filterCategory;
        const q = search.toLowerCase();
        const matchQ =
          !q ||
          t.title.toLowerCase().includes(q) ||
          String(t.displayId).toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q);
        return matchTab && matchP && matchCat && matchQ;
      }),
    [tickets, tabStatus, filterPriority, filterCategory, search],
  );

  const handleCreate = (t) => {
    setTickets((p) => [t, ...p]);
    pushToast(`Ticket ${t.displayId} created successfully.`);
  };

  const STAT_CARDS = [
    { label: "Total", key: "All", color: T.accent, bg: T.accentBg },
    { label: "Open", key: "Open", color: T.info, bg: T.infoBg },
    { label: "Closed", key: "Closed", color: T.textMuted, bg: T.bg },
  ];

  return (
    <>
      <Sidebar />
      <style>{GLOBAL_CSS}</style>

      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          padding: "0px 20px 20px 93px",
          fontFamily: T.font,
          color: T.text,
        }}
      >
        {/* Header */}
        <div
          style={{
            marginBottom: "22px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {/* HEADER */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              height: 70,
            }}
          >
            <img src={companylogo} alt="logo" />
            <button
              className="tkt-btn-primary"
              onClick={() => setCreateOpen(true)}
              style={primaryBtnStyle}
            >
              <Ic.Plus /> Raise a Ticket
            </button>
          </Stack>
        </div>

        {/* Toasts */}
        <Toast toasts={toasts} onDismiss={dismissToast} />

        {/* Stat cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "12px",
            marginBottom: "22px",
          }}
        >
          {STAT_CARDS.map((s) => (
            <div
              key={s.key}
              className="tkt-stat-card"
              onClick={() => setTabStatus(s.key)}
              style={{
                background: T.white,
                borderRadius: T.radiusSm,
                padding: "14px 16px",
                border: `1.5px solid ${tabStatus === s.key ? s.color : T.border}`,
                cursor: "pointer",
                boxShadow: T.shadow,
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 800,
                  color: s.color,
                  lineHeight: 1.1,
                }}
              >
                {stats[s.key]}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: T.textMuted,
                  marginTop: "4px",
                }}
              >
                {s.label}
              </div>
              {tabStatus === s.key && (
                <div
                  style={{
                    width: "20px",
                    height: "3px",
                    background: s.color,
                    borderRadius: "2px",
                    marginTop: "8px",
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Filters row */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "16px",
            alignItems: "center",
          }}
        >
          {/* Search */}
          <div
            style={{
              flex: "1 1 200px",
              minWidth: "140px",
              position: "relative",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: T.textFaint,
                pointerEvents: "none",
                display: "flex",
              }}
            >
              <Ic.Search />
            </span>
            <input
              className="tkt-input"
              style={{ ...inputStyle, paddingLeft: "36px" }}
              placeholder="Search by title, ID, category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Priority filter */}
          <select
            className="tkt-select"
            style={{
              ...selectStyle,
              width: "auto",
              minWidth: "130px",
              flex: "0 0 auto",
            }}
            value={filterPriority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="All">All Priorities</option>
            {PRIORITIES.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>

          {/* Category filter */}
          <select
            className="tkt-select"
            style={{
              ...selectStyle,
              width: "auto",
              minWidth: "160px",
              flex: "0 0 auto",
            }}
            value={filterCategory}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="All">All Categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.departmentId} value={c.label}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status tabs */}
        <div
          style={{
            display: "flex",
            gap: "2px",
            marginBottom: "14px",
            borderBottom: `1px solid ${T.border}`,
            overflowX: "auto",
            paddingBottom: "0",
          }}
        >
          {["All", ...STATUSES].map((s) => {
            const active = tabStatus === s;
            return (
              <button
                key={s}
                className="tkt-tab"
                onClick={() => setTabStatus(s)}
                style={{
                  padding: "9px 16px",
                  fontSize: "13.5px",
                  fontWeight: active ? 700 : 500,
                  color: active ? T.accent : T.textMuted,
                  borderBottom: `2.5px solid ${active ? T.accent : "transparent"}`,
                  whiteSpace: "nowrap",
                  background: "none",
                  border: "none",
                  borderBottomWidth: "2.5px",
                  borderBottomStyle: "solid",
                  borderBottomColor: active ? T.accent : "transparent",
                  cursor: "pointer",
                }}
              >
                {s}
                <span
                  style={{
                    marginLeft: "6px",
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "1px 6px",
                    borderRadius: "10px",
                    background: active ? T.accentBg : T.borderLight,
                    color: active ? T.accent : T.textFaint,
                  }}
                >
                  {s === "All"
                    ? tickets.length
                    : tickets.filter((t) => t.status === s).length}
                </span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div
          style={{
            background: T.white,
            borderRadius: T.radius,
            border: `1px solid ${T.border}`,
            boxShadow: T.shadow,
            overflow: "hidden",
          }}
        >
          <div className="tkt-table-wrap">
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "700px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "",
                    "Ticket ID",
                    "Title",
                    "Category",
                    "Priority",
                    "Status",
                    "Created",
                    "",
                  ].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        padding: i === 0 ? "12px 0 12px 6px" : "12px 14px",
                        textAlign: "left",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: T.textFaint,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        background: T.bg,
                        borderBottom: `1px solid ${T.border}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: "52px 20px",
                        textAlign: "center",
                        color: T.textFaint,
                        fontSize: "14px",
                      }}
                    >
                      Loading tickets…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: "52px 20px",
                        textAlign: "center",
                        color: T.textFaint,
                        fontSize: "14px",
                      }}
                    >
                      {search ||
                      filterPriority !== "All" ||
                      filterCategory !== "All"
                        ? "No tickets match your filters. Try adjusting the search or filters above."
                        : "No tickets here yet. Click 'Raise a Ticket' to get started."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((t) => {
                    const strip = PRIORITY[t.priority]?.strip || T.accent;
                    return (
                      <tr key={t.id} className="tkt-row">
                        {/* Priority strip — the signature element */}
                        <td
                          style={{
                            padding: "0 0 0 6px",
                            width: "10px",
                            borderBottom: `1px solid ${T.borderLight}`,
                          }}
                        >
                          <span
                            className="tkt-strip"
                            style={{
                              display: "block",
                              width: "3px",
                              height: "44px",
                              borderRadius: "2px",
                              background: strip,
                              transition: "width 0.15s",
                            }}
                          />
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: `1px solid ${T.borderLight}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "12.5px",
                              fontWeight: 700,
                              color: T.textFaint,
                              letterSpacing: "0.04em",
                            }}
                          >
                            {t.displayId}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: `1px solid ${T.borderLight}`,
                            maxWidth: "240px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "13.5px",
                              fontWeight: 600,
                              color: T.text,
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t.title}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: `1px solid ${T.borderLight}`,
                          }}
                        >
                          <CategoryBadge cat={t.category} />
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: `1px solid ${T.borderLight}`,
                          }}
                        >
                          <PriorityBadge p={t.priority} />
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: `1px solid ${T.borderLight}`,
                          }}
                        >
                          <StatusBadge s={t.status} />
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: `1px solid ${T.borderLight}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <span
                            style={{ fontSize: "12.5px", color: T.textFaint }}
                          >
                            {timeAgo(t.createdAt)}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: `1px solid ${T.borderLight}`,
                            textAlign: "right",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="tkt-icon-btn"
                            title="View"
                            onClick={() => handleViewTicket(t)}
                            style={iconBtnStyle}
                          >
                            {loadingRowId === t.id ? (
                              <Ic.Spinner />
                            ) : (
                              <Ic.Eye />
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <CreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        contactId={contactId}
      />

      {/* Detail Drawer */}
      <TicketDrawer
        ticket={activeTicket}
        onClose={() => setActive(null)}
        refreshing={ticketRefreshing}
      />
    </>
  );
}
